import React, { useState, useRef, useEffect } from 'react';

export interface FilterChoice {
  value: string;
  label: string;
}

export interface FilterGroup {
  id: string;
  label: string;
  type: 'select' | 'radio' | 'checkbox';
  choices: FilterChoice[];
}

interface FilterProps {
  label?: string;
  icon?: string;
  options: FilterGroup[] | FilterChoice[]; // Hỗ trợ cả danh sách phẳng và phân nhóm
  onFilterChange?: (key: string, value: string) => void;
  onChange?: (value: string) => void; // Hỗ trợ prop cũ cho các trang chưa cập nhật
  className?: string;
  activeCount?: number;
  selectedValues?: Record<string, string>;
  value?: string; // Hỗ trợ prop cũ cho các trang chưa cập nhật
}

/**
 * Filter - Thành phần lọc nâng cao chuẩn Landscape
 * Giao diện Premium với hiệu ứng Glassmorphism, bo góc rounded-2xl.
 * Tuân thủ hệ thống màu Semantic và Typography của Landscape (Base = 11.5px).
 * Hỗ trợ linh hoạt cả API mới (phân nhóm) và API cũ (danh sách phẳng).
 */
export const Filter = ({
  label = 'Bộ lọc',
  icon = 'ri-sound-module-line',
  options = [],
  onFilterChange,
  onChange,
  className = '',
  activeCount = 0,
  selectedValues = {},
  value
}: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Chuẩn hóa options thành FilterGroup[]
  const normalizedGroups: FilterGroup[] = React.useMemo(() => {
    if (!options || options.length === 0) return [];

    // Kiểm tra nếu là danh sách phẳng (không có thuộc tính 'choices')
    if (!('choices' in options[0])) {
      return [{
        id: 'default',
        label: label || 'Lọc',
        type: 'select',
        choices: options as FilterChoice[]
      }];
    }

    return options as FilterGroup[];
  }, [options, label]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (groupId: string, val: string) => {
    if (onFilterChange) onFilterChange(groupId, val);
    if (onChange) onChange(val);
  };

  const checkSelected = (groupId: string, val: string) => {
    if (selectedValues && selectedValues[groupId] === val) return true;
    if (value === val) return true;
    return false;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Nút bấm kích hoạt (Trigger) */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-md border transition-all duration-200 cursor-pointer
          ${isOpen
            ? 'bg-brand-50 border-brand-200 text-brand-700 shadow-sm'
            : 'bg-white border-slate-100 text-slate-700 hover:bg-slate-50 hover:border-brand-300 shadow-sm'
          }
        `}
      >
        <i className={`${icon} ${isOpen ? 'text-brand-600' : 'text-slate-400'} text-lg`}></i>
        <span className="sm:text-sm text-xs">{label}</span>

        {activeCount > 0 && (
          <span className="w-4 h-4 flex items-center justify-center bg-brand-600 text-white rounded-full text-xs animate-in zoom-in">
            {activeCount}
          </span>
        )}

        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180 text-brand-600' : 'text-slate-400'}`}></i>
      </button>

      {/* Dropdown Menu - Glassmorphism style */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-72 bg-white border border-slate-100 rounded-md shadow-sm z-50 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top-right">
          <div className="p-2 space-y-4 max-h-[450px] overflow-y-auto">
            {normalizedGroups.map((group) => (
              <div key={group.id} className="p-2">
                <h4 className="text-xs text-slate-400 tracking-wider mb-2.5 px-2 flex items-center justify-between tracking-widest">
                  {group.label}
                  <span className="flex-1 h-[1px] bg-slate-100 ml-3 opacity-50"></span>
                </h4>

                <div className="space-y-0.5">
                  {(group.choices || []).map((choice) => {
                    const isSelected = checkSelected(group.id, choice.value);
                    return (
                      <button
                        key={choice.value}
                        onClick={() => {
                          handleSelect(group.id, choice.value);
                        }}
                        className={`
                          w-full flex items-center justify-between px-3 py-2 sm:text-sm text-xs rounded-md transition-all group cursor-pointer
                          ${isSelected
                            ? 'bg-brand-50 text-brand-700'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-brand-600'
                          }
                        `}
                      >
                        {choice.label}
                        <div className={`
                          w-4 h-4 rounded-full border flex items-center justify-center transition-all
                          ${isSelected
                            ? 'bg-brand-600 border-brand-600 shadow-sm'
                            : 'bg-white border-slate-100 group-hover:border-brand-300'
                          }
                        `}>
                          {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3">
            <button
              onClick={() => setIsOpen(false)}
              className="sm:text-sm text-xs text-slate-400 hover:text-slate-700 px-2 py-1 transition-colors cursor-pointer"
            >
              Hủy bỏ
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="bg-brand-600 text-white sm:text-sm text-xs px-4 py-2 rounded-md shadow-sm hover:bg-brand-700 active:scale-95 transition-all cursor-pointer"
            >
              Áp dụng
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
